import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
    useMemo,
} from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Save,
    X,
    FileText,
    Plus,
    Trash2,
    Ship,
    User,
    MapPin,
    Package,
    Info,
    ChevronDown,
    ChevronUp,
    CreditCard,
    Printer,
    Building2,
    Hash,
    Check,
    Upload,
    Settings,
    Route,
    Weight,
    Send,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { GetHBLDetails, updateHBLDetails } from "@/redux/Booking/actions";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SearchBar } from "@/components/SearchBox/SearchBar";
import {
    getAddressAPI,
    getAddresswithfulldetail,
} from "@/redux/Purchase/actions";
import DateInputwithcal from "@/components/ui/DateInputwithcal";
import FileUpload from "@/components/FileUpload/FileUpload";
import { validateForm } from "@/components/ui/useFormValidation";
import UploadNotes from "@/components/NotesUpload/NotesUpload";
import { toast } from "sonner";
import ValidationHBLfrom from "@/validation/HBLfrom.json";
import {
    getClauselist,
    getEntitySuggestionSearchAPI,
    ENTITY_SUGGESTION_TYPES,
} from "@/redux/Common/actions";
import { callLoader } from "@/redux/Loader/actions";
import { date } from "zod";
import PageHeader from "@/components/FormHeader/formPageHeader";
import { InlineCountryFlag } from "@/components/shipment/CountryBadge";

const SECTION_COLORS = {
    blue: "bg-blue-500",
    cyan: "bg-cyan-500",
    violet: "bg-violet-500",
    purple: "bg-purple-500",
    orange: "bg-orange-500",
    teal: "bg-teal-500",
    green: "bg-emerald-500",
    indigo: "bg-indigo-500",
    slate: "bg-slate-500",
    pink: "bg-pink-600",
};

const SectionCard = React.forwardRef(({ children, className = "" }, ref) => (
    <div
        ref={ref}
        className={`bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200 ${className}`}
    >
        {children}
    </div>
));

const SectionHeader = ({
    icon: Icon,
    title,
    color = "blue",
    section,
    badge,
    expandedSections,
    toggleSection,
}) => {
    const iconBg = SECTION_COLORS[color] ?? SECTION_COLORS.blue;
    return (
        <button
            type="button"
            onClick={() => toggleSection(section)}
            className="w-full flex items-center justify-between p-4 border-b bg-gradient-to-r to-slate-100 dark:from-slate-800 dark:to-slate-700 cursor-pointer"
        >
            <div className="flex items-center gap-3">
                <div className={`p-2 mx-2 rounded-lg shadow-md ${iconBg}`}>
                    <Icon className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-200">
                    {title}
                </h2>
                {badge && (
                    <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-xs font-medium rounded-full">
                        {badge}
                    </span>
                )}
            </div>
            <div
                className={`p-1.5 rounded-md transition-colors ${expandedSections[section]
                    ? "bg-indigo-100 dark:bg-indigo-900/30"
                    : "bg-slate-200 dark:bg-slate-700"
                    }`}
            >
                {expandedSections[section] ? (
                    <ChevronUp className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                ) : (
                    <ChevronDown className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                )}
            </div>
        </button>
    );
};

const InputField = ({ label, required, children, className = "" }) => (
    <div className={className}>
        <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1.5">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        {children}
    </div>
);

const HBL = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const urlBookingId = searchParams.get("bookingId");
  const { bookingId: stateBookingId, FILENUMBER } = location.state || {};
  const bookingId = stateBookingId || urlBookingId;
  const {
    countryList,
    documentTypePurchaseList,
    documentTypeSalesList,
    incoTermsList,
    quantityTypeList,
    salesPersonList,
    uomTypeList,
    commodityGroupList,
    containerTypeList,
    operatorList,
    moveTypeList,
    currency_list,
    charges_basis_list,
    fileManagerList,
    packageTypeList,
  } = useSelector((state) => state.common);
  const [formData, setFormData] = useState();
  const [authUser] = JSON.parse(localStorage.getItem("authUser") ?? "[{}]");
  const userName = authUser?.username;
  const companyId = authUser?.companyId;
  const [rows, setRows] = useState([]);
  const [transshipPorts, setTransshipPorts] = useState([]);
  const [dateErrors, setDateErrors] = useState({});
  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const fileInfoRef = useRef(null);
  const partiesRef = useRef(null);
  const routingRef = useRef(null);
  const transshipmentRef = useRef(null);
  const particularsRef = useRef(null);
  const paymentPrintRef = useRef(null);
  const stepSectionMap = useMemo(
    () => [
      "fileInfo",
      "parties",
      "routing",
      "transshipment",
      "particulars",
      "paymentPrint",
    ],
    [],
  );
  const sectionRefs = useMemo(
    () => ({
      fileInfo: fileInfoRef,
      parties: partiesRef,
      routing: routingRef,
      transshipment: transshipmentRef,
      particulars: particularsRef,
      paymentPrint: paymentPrintRef,
    }),
    [],
  );
  const steps = useMemo(
    () => [
      "File Info",
      "Shipper/Consignee",
      "Routing",
      "Transshipment",
      "Cargo",
      "Payment & Print",
    ],
    [],
  );
  const [expandedSections, setExpandedSections] = useState({
    file: true,
    parties: true,
    routing: true,
    transshipment: true,
    particulars: true,
    payment: true,
    charges: true,
    printing: true,
  });
  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };
  const scrollToSection = (index) => {
    const key = stepSectionMap[index];
    const ref = sectionRefs[key];
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
      setExpandedSections((prev) => {
        const next = { ...prev };
        if (key === "fileInfo") next.file = true;
        if (key === "parties") next.parties = true;
        if (key === "routing") next.routing = true;
        if (key === "transshipment") next.transshipment = true;
        if (key === "particulars") next.particulars = true;
        if (key === "paymentPrint") {
          next.payment = true;
          next.printing = true;
        }
        return next;
      });
    }
  };
  useEffect(() => {
    const observers = [];
    stepSectionMap.forEach((key, index) => {
      const ref = sectionRefs[key];
      if (!ref?.current) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setCurrentStep(index);
        },
        { root: null, rootMargin: "-30% 0px -60% 0px", threshold: 0 },
      );
      observer.observe(ref.current);
      observers.push(observer);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [formData, stepSectionMap, sectionRefs]);
  const isSectionFilled = useCallback(
    (key) => {
      if (!formData) return false;
      switch (key) {
        case "fileInfo":
          return true;
        case "parties":
          return !!(formData?.Shipper && String(formData.Shipper).trim());
        case "routing":
          return !!(
            formData?.PortOfLoading && String(formData.PortOfLoading).trim()
          );
        case "transshipment":
          return true;
        case "particulars":
          return (
            Array.isArray(rows) && rows.some((r) => r && r.IS_DELETED === false)
          );
        case "paymentPrint":
          return true;
        default:
          return false;
      }
    },
    [formData, rows],
  );
  const initialHBLdetail = {
    FileManager: "",
    fileNumber: "",
    HBLNumber: "",
    ForwarderRefNo: "",
    CustomerPoNo: "",
    CarrierMBLNo: "",
    Shipper: "",
    ShipperAddress: "",
    Consignee: "",
    ConsigneeAddress: "",
    Notify: "",
    NotifyAddress: "",
    AlsoNotify: "",
    typeOfService: "Port To Port",
    PortOfLoading: "",
    PortOfLoadingCode: "",
    PortOfDischarge: "",
    PortOfDischargeCode: "",
    LoadingPierTerminal: "",
    MoveType: "PORT TO PORT",
    VesselName: "",
    Voyage: "",
    etd: "",
    containers: [
      {
        sealNo: "",
        portCity: "",
        eta: "",
        VesselName: "",
        etd: "",
        Voyage: "",
      },
    ],
    paymentMode: "Prepaid",
    releaseMode: "Original",
    DoNotAutoCalculate: true,
  };
  const initialrow = {
    ID: 0,
    BolID: 0,
    LINE_SEQ_NO: 0,
    CONTAINER_NUMBER: "",
    SEAL_NUMBER: "",
    MARKS_NO: "",
    CONT_SIZE: "",
    CONT_TYPE: "",
    CONT_TYPE_CODE: "",
    CONT_TYPE_NAME: "",
    TARE_WEIGHT: "",
    NO_PACKAGE: 0,
    PACKAGE_CODE: "",
    SCHEDULE_B: "",
    PACKAGE_DESCRIPTION: "",
    DESCRIPTIONS: "",
    KGS: 0,
    LBS: 0,
    CBM: 0,
    CFT: 0,
    RIDER_PAGE_NO: 0,
    IS_DELETED: false,
    HAZ_CODE: "",
    MANUFACTURER: "",
    COUNTRY: "",
    IT_NUMBER: "",
    CNC_PRS_NUMBER: "",
    PICK_UP_NUMBER: "",
    LAST_DATE: new Date().toISOString(),
    SENT_TO_RAIL_INC: false,
    SENT_DATE_TO_RAIL_INC: null,
    IS_APPLIED_ON_HBL: false,
    NET_KGS: 0,
    NET_LBS: 0,
    IS_MERGED_PROCESS_ID: 0,
    IS_SPLITTED_PROCESS_ID: 0,
    CONTAINER_PARENT_ID: 0,
    VIN_NUMBER: "",
    TITLE_NUMBER: "",
    CARGO_STATE: "",
    IMPORT_FROM_CARGO_UNIQUE_ID: 0,
    CREATED_DATE: new Date().toISOString(),
  };
  useEffect(() => {
    if (bookingId != 0 && bookingId != null && bookingId != undefined) {
      _getHBLdetail();
    } else {
      _setNewBooking();
    }
  }, [bookingId]);
  const _getHBLdetail = async () => {
    try {
      dispatch(callLoader(1));
      const resultData = await dispatch(GetHBLDetails(bookingId));
      setFormData(resultData);
      setTransshipPorts(resultData?.hblTranshipment ?? []);
      setFormData((prev) => ({
        ...prev,
        ModifiedBy:
          `${authUser?.firstName ?? ""} ${authUser?.lastName ?? ""}`.trim(),
        companyId: companyId,
        ModifiedOn: new Date().toISOString(),
        CreatedBy: resultData?.CreatedBy || userName,
        CreatedOn: resultData?.CreatedOn || new Date().toISOString(),
        DoNotAutoCalculate: resultData?.DoNotAutoCalculate ?? true,
      }));
      setRows(
        resultData.ContainersCargoModel.length > 0
          ? resultData.ContainersCargoModel
          : [initialrow],
      );
    } catch (error) {
      console.error("Error fetching HBL details:", error);
      toast.error("Failed to load HBL details");
    } finally {
      dispatch(callLoader(0));
    }
  };
  const _setNewBooking = async () => {
    setFormData(initialHBLdetail);
    setFormData({
      SHIPMENT_TYPE: "FCL",
      CREATED_BY: userName,
      companyId: companyId,
    });
  };
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

    // ─── Mutual-exclusion groups ───────────────────────────────────────────────
    // "Show as Consignee on MBL"  →  only one of these three can be checked
    const CONSIGNEE_ON_MBL_GROUP = [
        "ConsigneeCopyToMBL",
        "CopyToMBLNotifyAsConsignee",
        "CopyToMBLAgentAsConsignee",
    ];
    // "Show as Notify on MBL"  →  only one of these two can be checked
    const NOTIFY_ON_MBL_GROUP = ["NotifyCopyToMBL", "CopyToMBLConsigneeAsNotify", "DeliveryAgtCopyToMBL"];

    const handleMutualExclusiveCheck = (name, checked) => {
        if (CONSIGNEE_ON_MBL_GROUP.includes(name)) {
            setFormData((prev) => {
                const next = { ...prev };
                CONSIGNEE_ON_MBL_GROUP.forEach((key) => (next[key] = false));
                next[name] = checked;
                return next;
            });
        } else if (NOTIFY_ON_MBL_GROUP.includes(name)) {
            setFormData((prev) => {
                const next = { ...prev };
                NOTIFY_ON_MBL_GROUP.forEach((key) => (next[key] = false));
                next[name] = checked;
                return next;
            });
        } else {
            handleInputChange({
                target: { name, value: checked, type: "checkbox", checked },
            });
        }
    };
    // ──────────────────────────────────────────────────────────────────────────

  const addTransshipPort = () => {
    setTransshipPorts((prev) => [
      ...prev,
      {
        transPortId: 0,
        transPortCity: "",
        transEta: null,
        transActualDeparture: null,
        transVessel: "",
        transEtd: null,
        transVoyage: "",
        transActualArrival: null,
        isDeleted: false,
        createdDate: new Date().toISOString(),
        createdBy: userName,
      },
    ]);
  };
  const handleTranshipPortChange = (index, field, value) => {
    const updated = [...transshipPorts];
    updated[index][field] = value;
    setTransshipPorts(updated);
  };
useEffect(() => {
  if (formData?.DoNotAutoCalculate ?? true) return;
  calculateTotals();
}, [rows, formData?.DoNotAutoCalculate]);
  const calculateTotals = () => {
     if (formData?.DoNotAutoCalculate ?? true) return; 
    let totals = {
      containers: 0,
      packages: 0,
      netKgs: 0,
      netLbs: 0,
      kgs: 0,
      lbs: 0,
      cbm: 0,
      cft: 0,
    };
    rows
      .filter((r) => r.IS_DELETED === false)
      .forEach((r) => {
        totals.containers += 1;
        totals.packages += Number(r.NO_PACKAGE) || 0;
        totals.netKgs += Number(r.NET_KGS) || 0;
        totals.netLbs += Number(r.NET_LBS) || 0;
        totals.kgs += Number(r.KGS) || 0;
        totals.lbs += Number(r.LBS) || 0;
        totals.cbm += Number(r.CBM) || 0;
        totals.cft += Number(r.CFT) || 0;
      });
    setFormData((prev) => ({
      ...prev,
      TotalContainer: totals.containers,
      TotalPackage: totals.packages,
      TotalNetKgs: Number(totals.netKgs).toFixed(2),
      TotalNetLbs: Number(totals.netLbs).toFixed(2),
      TotalKgs: Number(totals.kgs).toFixed(2),
      TotalLbs: Number(totals.lbs).toFixed(2),
      TotalCbm: Number(totals.cbm).toFixed(2),
      TotalCft: Number(totals.cft).toFixed(2),
    }));
  };
  const _handleInputChange = (index, field, value) => {
    const updated = [...rows];
    updated[index][field] = value;
    setRows(updated);
  };
  const KG_TO_LBS = 2.20462;
  const LBS_TO_KG = 0.453592;
  const CBM_TO_CFT = 35.3147;
  const CFT_TO_CBM = 0.0283168;
  const handleWeightChange = (rowIndex, field, value) => {
    const num = parseFloat(value) || 0;

        setRows((prevRows) => {
            const updatedRows = [...prevRows];
            const row = { ...updatedRows[rowIndex], [field]: num };

    // ✅ skip cross-unit conversion when checkbox is ticked
    if (!(formData?.DoNotAutoCalculate ?? true) && !isNaN(num)) {
      switch (field) {
        case "NET_KGS":
          row.NET_LBS = Number((num || 0) * KG_TO_LBS).toFixed(2);
          break;
        case "NET_LBS":
          row.NET_KGS = Number((num || 0) * LBS_TO_KG).toFixed(2);
          break;
        case "KGS":
          row.LBS = Number((num || 0) * KG_TO_LBS).toFixed(2);
          break;
        case "LBS":
          row.KGS = Number((num || 0) * LBS_TO_KG).toFixed(2);
          break;
        case "CBM":
          row.CFT = Number((num || 0) * CBM_TO_CFT).toFixed(2);
          break;
        case "CFT":
          row.CBM = Number((num || 0) * CFT_TO_CBM).toFixed(2);
          break;
        default:
          break;
      }
    }

            updatedRows[rowIndex] = row;
            return updatedRows;
        });
    };
    const addRow = () => {
        setRows((prev) => {
            const nextId = prev.length + 1;
            return [
                ...prev,
                {
                    ...initialrow,
                    // ID: nextId,
                    LINE_SEQ_NO: nextId,
                    CONTAINER_NUMBER: "",
                    IS_DELETED: false,
                    CREATED_DATE: new Date().toISOString(),
                },
            ];
        });
    };
    const handleDateChange = (name, value) => {
        setFormData((prev) => ({
            ...prev,
            [name]: value ? value : null,
        }));
    };
    const handleDateError = (fieldName, hasError) => {
        setDateErrors((prev) => ({
            ...prev,
            [fieldName]: hasError,
        }));
    };
    const _setAddress = async (type, id) => {
        const address = await getAddressAPI(id);
        setFormData((prev) => ({ ...prev, [`${type}`]: address }));
    };
    const handleSave = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        try {
            dispatch(callLoader(1));
            const payload = {
                ...formData,
                ContainersCargoModel: rows,
                hblTranshipment: transshipPorts,
            };
            console.log("Payload:", payload);
            const validationErrors = validateForm({
                data: formData,
                items: [],
                rules: ValidationHBLfrom,
            });
            setErrors(validationErrors);
            if (Object.keys(validationErrors).length > 0) {
                return;
            }
            if (Object.values(dateErrors).some(Boolean)) {
                toast.error("Please correct the date fields");
                return;
            }

            const hblDetails = await dispatch(GetHBLDetails(bookingId));
            if (hblDetails?.lockhbl === true) {
                toast.error("HBL is already locked");
                return;
            }

            let response = await updateHBLDetails(payload);
            if (response?.statuscode === 200 || response?.statuscode === 201) {
                toast.success("HBL details saved successfully");
                return { success: true, data: response.data };
            }
            return { success: false, status: response?.status };
        } catch (error) {
            toast.error(
                error?.response?.data?.title ||
                error?.message ||
                "Something went wrong",
            );
            return {
                success: false,
                status: error?.response?.status,
                errors: error?.response?.data?.errors,
            };
        } finally {
            dispatch(callLoader(0));
        }
    };
    const _getPortCode = (strdata) => {
        if (!strdata) return "";
        const match = strdata.match(/\[(.*?)\]/);
        const code = match ? match[1] : "";
        console.log(code);
        return code;
    };
    const inputClass =
        "w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all";
    const selectClass =
        "w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-all appearance-none cursor-pointer";
    const readonlyinputClass =
        "w-full px-3 py-2 bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-700 dark:text-slate-300 placeholder-slate-400 cursor-not-allowed";
    return (
        <>
            <div className="page-header-sticky top-0 z-50">
                <PageHeader
                    icon={<FileText className="w-5 h-5 text-white" />}
                    title="House Bill of Lading"
                    subtitle={
                        FILENUMBER ? (
                            <>
                                {"File No. "}
                                <span className="text-slate-700 font-semibold dark:text-slate-300">
                                    {FILENUMBER}
                                </span>
                                {" · "}
                                <span className="font-mono">
                                    {formData?.HBLNumber || "NEW"}
                                </span>
                            </>
                        ) : (
                            "Create and manage HBL documentation"
                        )
                    }
                    actions={[
                        {
                            label: "Cancel",
                            icon: <X />,
                            onClick: () => navigate("/booking"),
                            variant: "ghost",
                        },
                        {
                            label: "Print",
                            icon: <Printer />,
                            onClick: (e) => {
                                e.stopPropagation();
                                window.open(
                                    `/hblPrint?bookingId=${bookingId}&fileNumber=${encodeURIComponent(FILENUMBER)}`,
                                    "_blank",
                                    "noopener,noreferrer",
                                );
                            },
                            variant: "tertiary",
                        },
                        "|",
                        {
                            label: "MBL",
                            onClick: (e) => {
                                e.stopPropagation();
                                navigate("/mbl", {
                                    state: {
                                        bookingId: bookingId,
                                        FILENUMBER: FILENUMBER,
                                    },
                                });
                            },
                        },
                    ]}
                    onSubmit={handleSave}
                    submitLabel="Submit HBL"
                />
                <div style={{ height: "var(--page-header-height)" }} />
            </div>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="min-h-screen sticky "
            >
                <div className="p-4 space-y-3 pb-16">
                    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-700/30 dark:border-slate-700 px-4 py-3 overflow-x-auto">
                        <div className="flex items-center w-full justify-between gap-0">
                            {steps.map((step, index) => {
                                const sectionKey = stepSectionMap[index];
                                const filled = isSectionFilled(sectionKey);
                                const done = filled && index !== currentStep;
                                const active = index === currentStep;
                                return (
                                    <React.Fragment key={step}>
                                        <div
                                            className={[
                                                "flex items-center gap-2 h-8 pl-2 pr-3 rounded-full text-xs font-medium cursor-pointer transition-all duration-200 select-none",
                                                done &&
                                                "text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20",
                                                active &&
                                                "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300",
                                                !done &&
                                                !active &&
                                                "text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800",
                                            ]
                                                .filter(Boolean)
                                                .join(" ")}
                                            onClick={() => scrollToSection(index)}
                                        >
                                            <span
                                                className={[
                                                    "w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0",
                                                    done && "bg-green-500 text-white",
                                                    active && "bg-blue-600 text-white",
                                                    !done &&
                                                    !active &&
                                                    "bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400",
                                                ]
                                                    .filter(Boolean)
                                                    .join(" ")}
                                            >
                                                {done ? <Check className="w-3 h-3" /> : index + 1}
                                            </span>
                                            <span className="hidden sm:inline">{step}</span>
                                        </div>
                                        {index < steps.length - 1 && (
                                            <div
                                                className={[
                                                    "flex-1 h-0.5 min-w-[8px] mx-1 rounded-full transition-colors duration-300",
                                                    done
                                                        ? "bg-green-400 dark:bg-green-600"
                                                        : "bg-slate-200 dark:bg-slate-700",
                                                ].join(" ")}
                                            />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>

                    <div
                        ref={fileInfoRef}
                        className="grid grid-cols-1 xl:grid-cols-2 gap-3"
                    >
                        <SectionCard>
                            <SectionHeader
                                icon={Info}
                                title="File Information"
                                color="blue"
                                section="file"
                                expandedSections={expandedSections}
                                toggleSection={toggleSection}
                            />
                            {expandedSections.file && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    className="p-5"
                                >
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                                        <InputField label="HBL Manager" required>
                                            <select
                                                name="FileManager"
                                                value={formData?.FileManager}
                                                onChange={handleInputChange}
                                                className={selectClass}
                                            >
                                                <option value="">Select File Manager</option>
                                                {fileManagerList.map((item, index) => (
                                                    <option key={index} value={item.id}>
                                                        {item.filemanager}
                                                    </option>
                                                ))}
                                            </select>
                                        </InputField>
                                        <InputField label="File Number">
                                            <input
                                                type="text"
                                                name="fileNumber"
                                                readOnly
                                                value={FILENUMBER}
                                                onChange={handleInputChange}
                                                className={readonlyinputClass}
                                            />
                                        </InputField>
                                        <InputField label="House BL Number">
                                            <input
                                                type="text"
                                                name="HBLNumber"
                                                value={formData?.HBLNumber}
                                                onChange={handleInputChange}
                                                className={inputClass}
                                            />
                                        </InputField>
                                        <InputField label="Forwarder Ref No.">
                                            <input
                                                type="text"
                                                name="ForwarderRefNo"
                                                value={formData?.ForwarderRefNo}
                                                onChange={handleInputChange}
                                                className={inputClass}
                                            />
                                        </InputField>
                                        <InputField label="Customer Ref #">
                                            <input
                                                type="text"
                                                name="CustomerPoNo"
                                                value={formData?.CustomerPoNo}
                                                onChange={handleInputChange}
                                                className={inputClass}
                                            />
                                        </InputField>
                                        <InputField label="Lock HBL:">
                                            <input
                                                type="checkbox"
                                                name="lockhbl"
                                                checked={!!formData?.lockhbl}
                                                onChange={handleInputChange}
                                                className="h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                                            />
                                        </InputField>

                                    </div>
                                </motion.div>
                            )}
                        </SectionCard>
                        <SectionCard>
                            <SectionHeader
                                icon={Info}
                                title="Carrier & References"
                                color="blue"
                                section="file"
                                expandedSections={expandedSections}
                                toggleSection={toggleSection}
                            />
                            {expandedSections.file && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    className="p-5"
                                >
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                                        <InputField label="Carrier Booking #" required>
                                            <input
                                                type="text"
                                                name="CarrierBookingNo"
                                                readOnly
                                                value={formData?.CarrierBookingNo}
                                                onChange={handleInputChange}
                                                className={inputClass}
                                            />
                                        </InputField>
                                        <InputField label="Carrier MBL Number">
                                            <input
                                                type="text"
                                                name="CarrierMBLNo"
                                                value={formData?.CarrierMBLNo}
                                                onChange={handleInputChange}
                                                className={inputClass}
                                            />
                                        </InputField>
                                        <InputField label="Export Ref. Number">
                                            <input
                                                type="text"
                                                name="ExportRefNumber"
                                                value={formData?.ExportRefNumber}
                                                onChange={handleInputChange}
                                                className={inputClass}
                                            />
                                        </InputField>
                                        <InputField label="ITN Number">
                                            <input
                                                type="text"
                                                name="ITNNumber"
                                                value={formData?.ITNNumber}
                                                onChange={handleInputChange}
                                                className={inputClass}
                                            />
                                        </InputField>
                                    </div>
                                </motion.div>
                            )}
                        </SectionCard>
                    </div>

                    {/* ── PARTIES ── */}
                    <SectionCard ref={partiesRef}>
                        <SectionHeader
                            icon={User}
                            title="Parties Information"
                            color="green"
                            section="parties"
                            expandedSections={expandedSections}
                            toggleSection={toggleSection}
                        />
                        {expandedSections.parties && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                className="p-5 space-y-2"
                            >
                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                                    {/* ── Shipper ── */}
                                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                                        <InputField label="Shipper/Exporter" required>
                                            <SearchBar
                                                searchAPI={getEntitySuggestionSearchAPI(
                                                    ENTITY_SUGGESTION_TYPES.EXPORTER,
                                                )}
                                                selectReacord={(d) => {
                                                    handleInputChange({
                                                        target: { name: "Shipper", value: d?.[1] },
                                                    });
                                                    handleInputChange({
                                                        target: { name: "ShipperID", value: d?.[0] },
                                                    });
                                                    _setAddress("ShipperAddress", d?.[0]);
                                                }}
                                                onchange={(val) => {
                                                    if (!val) {
                                                        handleInputChange({
                                                            target: { name: "Shipper", value: "" },
                                                        });
                                                        handleInputChange({
                                                            target: { name: "ShipperID", value: 0 },
                                                        });
                                                    }
                                                }}
                                                value={formData?.Shipper || ""}
                                            />
                                        </InputField>
                                        <textarea
                                            name="ShipperAddress"
                                            value={formData?.ShipperAddress}
                                            onChange={handleInputChange}
                                            rows="3"
                                            placeholder="Enter shipper address details..."
                                            className={`${inputClass} mt-2`}
                                        />
                                    </div>

                                    {/* ── Consignee ── */}
                                    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
                                        <div>
                                            <div className="flex items-center justify-between mb-1.5">
                                                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400">
                                                    Consignee: <span className="text-red-500">*</span>
                                                </label>
                                                <div className="flex items-center gap-2">
                                                    {/* Show as Notify on MBL — group: NOTIFY_ON_MBL_GROUP */}
                                                    <input
                                                        className="h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                                                        type="checkbox"
                                                        name="CopyToMBLConsigneeAsNotify"
                                                        checked={
                                                            formData?.CopyToMBLConsigneeAsNotify || false
                                                        }
                                                        onChange={(e) =>
                                                            handleMutualExclusiveCheck(
                                                                "CopyToMBLConsigneeAsNotify",
                                                                e.target.checked,
                                                            )
                                                        }
                                                    />
                                                    <label className="text-xs text-slate-600 dark:text-slate-400 mr-12">
                                                        Show as Notify on MBL
                                                    </label>
                                                    {/* Show as Consignee on MBL — group: CONSIGNEE_ON_MBL_GROUP */}
                                                    <input
                                                        className="h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                                                        type="checkbox"
                                                        name="ConsigneeCopyToMBL"
                                                        checked={formData?.ConsigneeCopyToMBL || false}
                                                        onChange={(e) =>
                                                            handleMutualExclusiveCheck(
                                                                "ConsigneeCopyToMBL",
                                                                e.target.checked,
                                                            )
                                                        }
                                                    />
                                                    <label className="text-xs text-slate-600 dark:text-slate-400">
                                                        Show as Consignee on MBL
                                                    </label>
                                                </div>
                                            </div>
                                            <SearchBar
                                                searchAPI={getEntitySuggestionSearchAPI(
                                                    ENTITY_SUGGESTION_TYPES.CONSIGNEE,
                                                )}
                                                selectReacord={(d) => {
                                                    handleInputChange({
                                                        target: { name: "Consignee", value: d?.[1] },
                                                    });
                                                    handleInputChange({
                                                        target: { name: "ConsigneeID", value: d?.[0] },
                                                    });
                                                    _setAddress("ConsigneeAddress", d?.[0]);
                                                }}
                                                onchange={(val) => {
                                                    if (!val) {
                                                        handleInputChange({
                                                            target: { name: "Consignee", value: "" },
                                                        });
                                                        handleInputChange({
                                                            target: { name: "ConsigneeID", value: 0 },
                                                        });
                                                    }
                                                }}
                                                value={formData?.Consignee || ""}
                                            />
                                        </div>
                                        <textarea
                                            name="ConsigneeAddress"
                                            value={formData?.ConsigneeAddress}
                                            onChange={handleInputChange}
                                            rows="3"
                                            placeholder="To the Order of: SHIPPER"
                                            className={`${inputClass} mt-2`}
                                        />
                                    </div>

                                    {/* ── Notify Party ── */}
                                    <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-200 dark:border-purple-800">
                                        <div>
                                            <div className="flex items-center justify-between mb-1.5">
                                                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400">
                                                    Notify Party:
                                                </label>
                                                <div className="flex items-center gap-2">
                                                    {/* Show as Consignee on MBL — group: CONSIGNEE_ON_MBL_GROUP */}
                                                    <input
                                                        className="h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                                                        type="checkbox"
                                                        name="CopyToMBLNotifyAsConsignee"
                                                        checked={
                                                            formData?.CopyToMBLNotifyAsConsignee || false
                                                        }
                                                        onChange={(e) =>
                                                            handleMutualExclusiveCheck(
                                                                "CopyToMBLNotifyAsConsignee",
                                                                e.target.checked,
                                                            )
                                                        }
                                                    />
                                                    <label className="text-xs text-slate-600 dark:text-slate-400 mr-12">
                                                        Show as Consignee on MBL
                                                    </label>
                                                    {/* Show as Notify on MBL — group: NOTIFY_ON_MBL_GROUP */}
                                                    <input
                                                        className="h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                                                        type="checkbox"
                                                        name="NotifyCopyToMBL"
                                                        checked={formData?.NotifyCopyToMBL || false}
                                                        onChange={(e) =>
                                                            handleMutualExclusiveCheck(
                                                                "NotifyCopyToMBL",
                                                                e.target.checked,
                                                            )
                                                        }
                                                    />
                                                    <label className="text-xs text-slate-600 dark:text-slate-400">
                                                        Show as Notify on MBL
                                                    </label>
                                                </div>
                                            </div>
                                            <SearchBar
                                                searchAPI={getEntitySuggestionSearchAPI(ENTITY_SUGGESTION_TYPES.NOTIFY)}
                                                selectReacord={(d) => {
                                                    handleInputChange({ target: { name: 'Notify', value: d?.[1] } });
                                                    handleInputChange({ target: { name: 'NotifyID', value: d?.[0] } });
                                                    _setAddress('NotifyAddress', d?.[0]);
                                                }}
                                                onchange={(val) => {
                                                    if (!val) {
                                                        handleInputChange({ target: { name: 'Notify', value: '' } });
                                                        handleInputChange({ target: { name: 'NotifyID', value: 0 } });
                                                    }
                                                }}
                                                value={formData?.Notify || ''}
                                            />
                                        </div>
                                        <textarea
                                            name="NotifyAddress"
                                            value={formData?.NotifyAddress}
                                            onChange={handleInputChange}
                                            rows="3"
                                            placeholder="Notify party address..."
                                            className={`${inputClass} mt-2`}
                                        />
                                    </div>

                                    {/* ── Also Notify Party ── */}
                                    <div className="p-4 bg-sky-50 dark:bg-sky-900/20 rounded-xl border border-sky-200 dark:border-sky-800">
                                        <div>
                                            <div className="flex items-center justify-between mb-1.5">
                                                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400">
                                                    Also Notify Party:
                                                </label>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        className="h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                                                        type="checkbox"
                                                        name="NotifyAlsoCopyToMBL"
                                                        checked={formData?.NotifyAlsoCopyToMBL || false}
                                                        onChange={handleInputChange}
                                                    />
                                                    <label className="text-xs text-slate-600 dark:text-slate-400">
                                                        Show as Also Notify on MBL
                                                    </label>
                                                </div>
                                            </div>
                                            <SearchBar
                                                searchAPI={getEntitySuggestionSearchAPI(
                                                    ENTITY_SUGGESTION_TYPES.CONSIGNEE,
                                                )}
                                                selectReacord={(d) => {
                                                    handleInputChange({
                                                        target: { name: "AlsoNotify", value: d?.[1] },
                                                    });
                                                    handleInputChange({
                                                        target: { name: "AlsoNotifyID", value: d?.[0] },
                                                    });
                                                    _setAddress("AlsoNotifyAddress", d?.[0]);
                                                }}
                                                onchange={(val) => {
                                                    if (!val) {
                                                        handleInputChange({
                                                            target: { name: "AlsoNotify", value: "" },
                                                        });
                                                        handleInputChange({
                                                            target: { name: "AlsoNotifyID", value: 0 },
                                                        });
                                                    }
                                                }}
                                                value={formData?.AlsoNotify || ""}
                                            />
                                        </div>
                                        <textarea
                                            name="AlsoNotifyAddress"
                                            value={formData?.AlsoNotifyAddress}
                                            onChange={handleInputChange}
                                            rows="3"
                                            placeholder="Also Notify party address..."
                                            className={`${inputClass} mt-2`}
                                        />
                                    </div>

                                    {/* ── Forwarding Agent ── */}
                                    <div className="p-4 bg-indigo-50 dark:bg-indigo-900/40 rounded-xl border border-indigo-200 dark:border-indigo-800">
                                        <div>
                                            <div className="flex items-center justify-between mb-1.5">
                                                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400">
                                                    Forwarding Agent:
                                                </label>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        className="h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                                                        type="checkbox"
                                                        name="ForwarderCopyToMBL"
                                                        checked={formData?.ForwarderCopyToMBL || false}
                                                        onChange={handleInputChange}
                                                    />
                                                    <label className="text-xs text-slate-600 dark:text-slate-400">
                                                        Show as Forwarding Agent on MBL
                                                    </label>
                                                </div>
                                            </div>
                                            <SearchBar
                                                searchAPI={getEntitySuggestionSearchAPI(
                                                    ENTITY_SUGGESTION_TYPES.FORWARDER,
                                                )}
                                                selectReacord={(d) => {
                                                    handleInputChange({
                                                        target: { name: "ForwardingAgent", value: d?.[1] },
                                                    });
                                                    handleInputChange({
                                                        target: {
                                                            name: "ForwardingAgentID",
                                                            value: d?.[0],
                                                        },
                                                    });
                                                    _setAddress("ForwardingAgentAddress", d?.[0]);
                                                }}
                                                onchange={(val) => {
                                                    if (!val) {
                                                        handleInputChange({
                                                            target: { name: "ForwardingAgent", value: "" },
                                                        });
                                                        handleInputChange({
                                                            target: { name: "ForwardingAgentID", value: 0 },
                                                        });
                                                    }
                                                }}
                                                value={formData?.ForwardingAgent || ""}
                                            />
                                        </div>
                                        <textarea
                                            name="ForwardingAgentAddress"
                                            value={formData?.ForwardingAgentAddress}
                                            onChange={handleInputChange}
                                            rows="3"
                                            placeholder="Forwarding Agent address..."
                                            className={`${inputClass} mt-2`}
                                        />
                                    </div>

                                    {/* ── Ultimate Consignee ── */}
                                    <div className="p-4 bg-teal-50 dark:bg-teal-900/20 rounded-xl border border-teal-200 dark:border-teal-800">
                                        <div>
                                            <div className="flex items-center justify-between mb-1.5">
                                                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400">
                                                    Ultimate Consignee:
                                                </label>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        className="h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                                                        type="checkbox"
                                                        name="UltimateConsigneeCopyToMBL"
                                                        checked={
                                                            formData?.UltimateConsigneeCopyToMBL || false
                                                        }
                                                        onChange={handleInputChange}
                                                    />
                                                    <label className="text-xs text-slate-600 dark:text-slate-400">
                                                        Show as Ultimate Consignee on MBL
                                                    </label>
                                                </div>
                                            </div>
                                            <SearchBar
                                                searchAPI={getEntitySuggestionSearchAPI(
                                                    ENTITY_SUGGESTION_TYPES.CONSIGNEE,
                                                )}
                                                selectReacord={(d) => {
                                                    handleInputChange({
                                                        target: {
                                                            name: "UltimateConsignee",
                                                            value: d?.[1],
                                                        },
                                                    });
                                                    handleInputChange({
                                                        target: {
                                                            name: "UltimateConsigneeID",
                                                            value: d?.[0],
                                                        },
                                                    });
                                                    _setAddress("UltimateConsigneeAddress", d?.[0]);
                                                }}
                                                onchange={(val) => {
                                                    if (!val) {
                                                        handleInputChange({
                                                            target: { name: "UltimateConsignee", value: "" },
                                                        });
                                                        handleInputChange({
                                                            target: { name: "UltimateConsigneeID", value: 0 },
                                                        });
                                                    }
                                                }}
                                                value={formData?.UltimateConsignee || ""}
                                            />
                                        </div>
                                        <textarea
                                            name="UltimateConsigneeAddress"
                                            value={formData?.UltimateConsigneeAddress}
                                            onChange={handleInputChange}
                                            rows="3"
                                            placeholder="Ultimate Consignee address..."
                                            className={`${inputClass} mt-2`}
                                        />
                                    </div>

                                    {/* ── Destination Agent ── */}
                                    <div className="p-4 bg-lime-50 dark:bg-lime-900/20 rounded-xl border border-purple-200 dark:border-lime-800">
                                        <div>
                                            <div className="flex items-center justify-between mb-1.5">
                                                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400">
                                                    Destination Agent:
                                                </label>
                                                <div className="flex items-center gap-2">
                                                    {/* Show as Consignee on MBL — group: CONSIGNEE_ON_MBL_GROUP */}
                                                    <input
                                                        className="h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                                                        type="checkbox"
                                                        name="CopyToMBLAgentAsConsignee"
                                                        checked={
                                                            formData?.CopyToMBLAgentAsConsignee || false
                                                        }
                                                        onChange={(e) =>
                                                            handleMutualExclusiveCheck(
                                                                "CopyToMBLAgentAsConsignee",
                                                                e.target.checked,
                                                            )
                                                        }
                                                    />
                                                    <label className="text-xs text-slate-600 dark:text-slate-400 mr-4">
                                                        Show as Consignee on MBL
                                                    </label>
                                                    {/* This one is independent — just copy to MBL as dest agent */}
                                                    <input
                                                        className="h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                                                        type="checkbox"
                                                        name="DeliveryAgtCopyToMBL"
                                                        checked={formData?.DeliveryAgtCopyToMBL || false}
                                                        onChange={(e) =>
                                                            handleMutualExclusiveCheck(
                                                                "DeliveryAgtCopyToMBL",
                                                                e.target.checked,
                                                            )
                                                        }
                                                    />
                                                    <label className="text-xs text-slate-600 dark:text-slate-400">
                                                        Show as Notify on MBL
                                                    </label>
                                                </div>
                                            </div>
                                            <SearchBar
                                                searchAPI={getEntitySuggestionSearchAPI(
                                                    ENTITY_SUGGESTION_TYPES.AGENT,
                                                )}
                                                selectReacord={(d) => {
                                                    handleInputChange({
                                                        target: { name: "DeliveryAgent", value: d?.[1] },
                                                    });
                                                    handleInputChange({
                                                        target: { name: "DeliveryID", value: d?.[0] },
                                                    });
                                                    _setAddress("DeliveryAddress", d?.[0]);
                                                }}
                                                onchange={(val) => {
                                                    if (!val) {
                                                        handleInputChange({
                                                            target: { name: "DeliveryAgent", value: "" },
                                                        });
                                                        handleInputChange({
                                                            target: { name: "DeliveryID", value: 0 },
                                                        });
                                                    }
                                                }}
                                                value={formData?.DeliveryAgent || ""}
                                            />
                                        </div>
                                        <textarea
                                            name="DeliveryAddress"
                                            value={formData?.DeliveryAddress}
                                            onChange={handleInputChange}
                                            rows="3"
                                            placeholder="Notify party address..."
                                            className={`${inputClass} mt-2`}
                                        />
                                    </div>

                                    {/* ── FMC / Pre Alert ── */}
                                    <div className="p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-xl border border-cyan-200 dark:border-cyan-800">
                                        <InputField label="FMC Licence Number">
                                            <input
                                                type="text"
                                                name="FmcNo"
                                                value={formData?.FmcNo}
                                                onChange={handleInputChange}
                                                className={inputClass}
                                            />
                                        </InputField>
                                        <div className="flex items-center gap-2 mt-8">
                                            <label className="block text-sm font-medium m-1.5 text-red-500 dark:text-red-500">
                                                Pre Alert
                                            </label>
                                            <input
                                                type="checkbox"
                                                name="IsPreAlert"
                                                checked={formData?.IsPreAlert || false}
                                                onChange={handleInputChange}
                                                className="h-4 w-4 text-blue-600 border-gray-300"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </SectionCard>

                    {/* ── ROUTING ── */}
                    <div
                        ref={routingRef}
                        className="grid grid-cols-1 xl:grid-cols-2 gap-6"
                    >
                        <SectionCard>
                            <SectionHeader
                                icon={Route}
                                title="Routing Information"
                                color="cyan"
                                section="routing"
                                expandedSections={expandedSections}
                                toggleSection={toggleSection}
                            />
                            {expandedSections.routing && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    className="p-5"
                                >
                                    <div className="mb-6 px-4 sm:px-5 py-4 bg-gradient-to-br from-slate-50 to-white dark:from-slate-800/50 dark:to-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm">
                                        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3">
                                            <p className="text-[10px] font-semibold tracking-widest text-slate-400 dark:text-slate-500 uppercase">
                                                Origin
                                            </p>
                                            <div />
                                            <p className="text-[10px] font-semibold tracking-widest text-slate-400 dark:text-slate-500 uppercase text-right">
                                                Destination
                                            </p>
                                        </div>
                                        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3 mt-1.5">
                                            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-md shadow-blue-400/30 ring-2 ring-white dark:ring-slate-900">
                                                {formData?.PortOfLoadingCode ? (
                                                    <InlineCountryFlag
                                                        code={formData?.PortOfLoadingCode}
                                                        className="w-5 h-5"
                                                    />
                                                ) : (
                                                    <MapPin className="w-5 h-5 text-white" />
                                                )}
                                            </div>
                                            <div className="relative h-11 flex items-center min-w-0">
                                                <div className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                                                <div className="flex-1 h-[3px] rounded-full bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400" />
                                                <div className="w-1.5 h-1.5 rounded-full bg-teal-400 flex-shrink-0" />
                                                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-700 flex items-center justify-center shadow-md shadow-cyan-300/20">
                                                    <Ship className="w-4 h-4 text-cyan-500" />
                                                </div>
                                            </div>
                                            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center shadow-md shadow-teal-400/30 ring-2 ring-white dark:ring-slate-900">
                                                {formData?.PortOfDischargeCode ? (
                                                    <InlineCountryFlag
                                                        code={formData?.PortOfDischargeCode}
                                                        className="w-5 h-5"
                                                    />
                                                ) : (
                                                    <MapPin className="w-5 h-5 text-white" />
                                                )}
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-[auto_1fr_auto] items-start gap-3 mt-2">
                                            <p className="max-w-[16ch] text-xs sm:text-sm font-bold text-slate-900 dark:text-white truncate">
                                                {formData?.PortOfLoading || "Port of Loading"}
                                            </p>
                                            <div />
                                            <p className="max-w-[16ch] text-xs sm:text-sm font-bold text-slate-900 dark:text-white truncate text-right">
                                                {formData?.PortOfDischarge || "Port of Discharge"}
                                            </p>
                                        </div>
                                        {formData?.MoveType ? (
                                            <div className="flex justify-center mt-3">
                                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 text-[11px] font-medium">
                                                    <Ship className="w-3 h-3" />
                                                    {formData.MoveType}
                                                </span>
                                            </div>
                                        ) : null}
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                                        <InputField label="Place of Receipt">
                                            <SearchBar
                                                searchAPI={
                                                    "v1/AutoSuggestion/GetDestinationSuggestion?Prefix="
                                                }
                                                paramCompanyId="companyId"
                                                selectReacord={(d) => {
                                                    handleInputChange({
                                                        target: { name: "PalceOfReceipt", value: d?.[1] },
                                                    });
                                                    handleInputChange({
                                                        target: {
                                                            name: "PalceOfReceiptCode",
                                                            value: _getPortCode(d?.[1]),
                                                        },
                                                    });
                                                }}
                                                onchange={(val) => {
                                                    if (!val) {
                                                        handleInputChange({
                                                            target: { name: "PalceOfReceipt", value: "" },
                                                        });
                                                        handleInputChange({
                                                            target: { name: "PalceOfReceiptCode", value: "" },
                                                        });
                                                    }
                                                }}
                                                value={formData?.PalceOfReceipt}
                                            />
                                        </InputField>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1.5">
                                                Show on HBL:
                                                <span className="text-red-300 text-xs">
                                                    *Use customized Loc. for AFR
                                                </span>
                                            </label>
                                            <input
                                                type="text"
                                                name="PalceOfReceiptCustomized"
                                                value={formData?.PalceOfReceiptCustomized}
                                                onChange={handleInputChange}
                                                className={inputClass}
                                            />
                                        </div>
                                        <InputField label="Port of Loading" required>
                                            <SearchBar
                                                searchAPI={
                                                    "v1/AutoSuggestion/GetDestinationSuggestion?Prefix="
                                                }
                                                paramCompanyId="companyId"
                                                selectReacord={(d) => {
                                                    handleInputChange({
                                                        target: { name: "PortOfLoading", value: d?.[1] },
                                                    });
                                                    handleInputChange({
                                                        target: {
                                                            name: "PortOfLoadingCode",
                                                            value: _getPortCode(d?.[1]),
                                                        },
                                                    });
                                                }}
                                                onchange={(val) => {
                                                    if (!val) {
                                                        handleInputChange({
                                                            target: { name: "PortOfLoading", value: "" },
                                                        });
                                                        handleInputChange({
                                                            target: {
                                                                name: "PORT_OF_LOADING_CODE",
                                                                value: "",
                                                            },
                                                        });
                                                    }
                                                }}
                                                value={formData?.PortOfLoading}
                                            />
                                        </InputField>
                                        <InputField label="Show on HBL:">
                                            <input
                                                type="text"
                                                name="PortOfLoadingCustomized"
                                                value={formData?.PortOfLoadingCustomized}
                                                onChange={handleInputChange}
                                                className={inputClass}
                                            />
                                        </InputField>
                                        <InputField label="Port of Discharge" required>
                                            <SearchBar
                                                searchAPI={
                                                    "v1/AutoSuggestion/GetDestinationSuggestion?Prefix="
                                                }
                                                paramCompanyId="companyId"
                                                selectReacord={(d) => {
                                                    handleInputChange({
                                                        target: { name: "PortOfDischarge", value: d?.[1] },
                                                    });
                                                    handleInputChange({
                                                        target: {
                                                            name: "PortOfDischargeCode",
                                                            value: _getPortCode(d?.[1]),
                                                        },
                                                    });
                                                }}
                                                onchange={(val) => {
                                                    if (!val) {
                                                        handleInputChange({
                                                            target: { name: "PORT_OF_DISCHARGE", value: "" },
                                                        });
                                                        handleInputChange({
                                                            target: {
                                                                name: "PortOfDischargeCode",
                                                                value: "",
                                                            },
                                                        });
                                                    }
                                                }}
                                                value={formData?.PortOfDischarge}
                                            />
                                        </InputField>
                                        <InputField label="Show on HBL:">
                                            <input
                                                type="text"
                                                name="PortOfDischargeCustomized"
                                                value={formData?.PortOfDischargeCustomized}
                                                onChange={handleInputChange}
                                                className={inputClass}
                                            />
                                        </InputField>
                                        <InputField label="Place of Delivery">
                                            <SearchBar
                                                searchAPI={
                                                    "v1/AutoSuggestion/GetDestinationSuggestion?Prefix="
                                                }
                                                paramCompanyId="companyId"
                                                selectReacord={(d) => {
                                                    handleInputChange({
                                                        target: { name: "PlaceOfDelivery", value: d?.[1] },
                                                    });
                                                    handleInputChange({
                                                        target: {
                                                            name: "PlaceOfDeliveryCode",
                                                            value: _getPortCode(d?.[1]),
                                                        },
                                                    });
                                                }}
                                                onchange={(val) => {
                                                    if (!val) {
                                                        handleInputChange({
                                                            target: { name: "PlaceOfDelivery", value: "" },
                                                        });
                                                        handleInputChange({
                                                            target: {
                                                                name: "PlaceOfDeliveryCode",
                                                                value: "",
                                                            },
                                                        });
                                                    }
                                                }}
                                                value={formData?.PlaceOfDelivery}
                                            />
                                        </InputField>
                                        <InputField label="Show on HBL:">
                                            <input
                                                type="text"
                                                name="PlaceOfDeliveryCustomized"
                                                value={formData?.PlaceOfDeliveryCustomized}
                                                onChange={handleInputChange}
                                                className={inputClass}
                                            />
                                        </InputField>
                                    </div>
                                </motion.div>
                            )}
                        </SectionCard>
                        <SectionCard>
                            <SectionHeader
                                icon={Route}
                                title="Routing Information"
                                color="cyan"
                                section="routing"
                                expandedSections={expandedSections}
                                toggleSection={toggleSection}
                            />
                            {expandedSections.routing && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    className="p-5"
                                >
                                    <div className="py-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                                        <InputField label="Vessel" required>
                                            <SearchBar
                                                searchAPI={"v1/AutoSuggestion/GetVesselList?Prefix="}
                                                paramCompanyId="companyId"
                                                selectReacord={(d) => {
                                                    handleInputChange({
                                                        target: { name: "VesselName", value: d?.[1] },
                                                    });
                                                    handleInputChange({
                                                        target: {
                                                            name: "VesselId",
                                                            value: String(d?.[0] ?? ""),
                                                        },
                                                    });
                                                }}
                                                onchange={(val) => {
                                                    if (!val) {
                                                        handleInputChange({
                                                            target: { name: "VesselName", value: "" },
                                                        });
                                                        handleInputChange({
                                                            target: { name: "VesselId", value: 0 },
                                                        });
                                                    }
                                                }}
                                                value={formData?.VesselName}
                                            />
                                        </InputField>
                                        <InputField label="Voyage" required>
                                            <input
                                                type="text"
                                                name="Voyage"
                                                value={formData?.Voyage}
                                                onChange={handleInputChange}
                                                className={inputClass}
                                            />
                                        </InputField>
                                        <InputField label="Move Type" required>
                                            <select
                                                name="MoveType"
                                                value={formData?.MoveType || ""}
                                                onChange={handleInputChange}
                                                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                            >
                                                <option value="">SELECT TYPE</option>
                                                {moveTypeList.map((item, index) => (
                                                    <option key={index} value={item.moveType}>
                                                        {item.moveType}
                                                    </option>
                                                ))}
                                            </select>
                                        </InputField>
                                        <InputField label="Hide on HBL">
                                            <input
                                                type="checkbox"
                                                name="MoveTypeHideOnHBL"
                                                checked={formData?.MoveTypeHideOnHBL || false}
                                                onChange={handleInputChange}
                                                className="h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                                            />
                                        </InputField>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-4">
                                        <InputField label="Carrier" required>
                                            <SearchBar
                                                searchAPI={
                                                    "v1/AutoSuggestion/GetCarrierSuggestion?Prefix="
                                                }
                                                paramCompanyId="companyId"
                                                selectReacord={(d) => {
                                                    handleInputChange({
                                                        target: { name: "CarrierName", value: d?.[1] },
                                                    });
                                                    handleInputChange({
                                                        target: {
                                                            name: "CarrierID",
                                                            value: String(d?.[0] ?? ""),
                                                        },
                                                    });
                                                    handleInputChange({
                                                        target: {
                                                            name: "CARRIER_SCAC",
                                                            value: String(d?.[2] ?? ""),
                                                        },
                                                    });
                                                }}
                                                onchange={(val) => {
                                                    if (!val) {
                                                        handleInputChange({
                                                            target: { name: "CarrierName", value: "" },
                                                        });
                                                        handleInputChange({
                                                            target: { name: "CARRIER_ID", value: 0 },
                                                        });
                                                    }
                                                }}
                                                value={formData?.CarrierName}
                                            />
                                        </InputField>
                                        <InputField label="Pre-Carrage By:">
                                            <input
                                                type="text"
                                                name="PreCarrageBy"
                                                value={formData?.PreCarrageBy}
                                                onChange={handleInputChange}
                                                className={inputClass}
                                            />
                                        </InputField>
                                        <InputField label="Country of Origin">

                                            <select
                                                className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                                value={formData?.CountryOfOrigin || ""}
                                                onChange={handleInputChange}
                                                name="CountryOfOrigin"
                                            >
                                                <option>Select Country</option>
                                                {countryList?.map((country, key) => (
                                                    <option key={key} value={country.incoterm}>
                                                        {country.incoterm}
                                                    </option>
                                                ))}
                                            </select>
                                        </InputField>
                                        <InputField label="Loading Pier / Terminal:">
                                            <input
                                                type="text"
                                                name="LoadingPierTerminal"
                                                value={formData?.LoadingPierTerminal}
                                                onChange={handleInputChange}
                                                className={inputClass}
                                            />
                                        </InputField>
                                    </div>
                                </motion.div>
                            )}
                        </SectionCard>
                    </div>

                    {/* ── TRANSSHIPMENT ── */}
                    <SectionCard ref={transshipmentRef}>
                        <SectionHeader
                            icon={Ship}
                            title="Transshipment Ports"
                            color="slate"
                            section="transshipment"
                            badge={`${transshipPorts?.length} port(s)`}
                            expandedSections={expandedSections}
                            toggleSection={toggleSection}
                        />
                        {expandedSections.transshipment && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                className="p-5"
                            >
                                <div className="">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                                                <th className="px-2 py-2 text-left text-sm font-semibold text-slate-600 dark:text-slate-400">
                                                    SNO
                                                </th>
                                                <th className="px-2 py-2 text-left text-sm font-semibold text-slate-600 dark:text-slate-400">
                                                    PORT/CITY
                                                </th>
                                                <th className="px-2 py-2 text-left text-sm font-semibold text-slate-600 dark:text-slate-400">
                                                    ETA
                                                </th>
                                                <th className="px-2 py-2 text-left text-sm font-semibold text-slate-600 dark:text-slate-400">
                                                    VESSEL
                                                </th>
                                                <th className="px-2 py-2 text-left text-sm font-semibold text-slate-600 dark:text-slate-400">
                                                    ETD
                                                </th>
                                                <th className="px-2 py-2 text-left text-sm font-semibold text-slate-600 dark:text-slate-400">
                                                    VOYAGE
                                                </th>
                                                <th className="px-2 py-2 text-left text-sm font-semibold text-slate-600 dark:text-slate-400"></th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                            {transshipPorts.map(
                                                (port, index) =>
                                                    port?.isDeleted == false && (
                                                        <tr
                                                            key={index}
                                                            className="hover:bg-slate-50 dark:hover:bg-slate-800/30"
                                                        >
                                                            <td className="px-2 py-2 text-slate-600 dark:text-slate-400">
                                                                {index + 1}
                                                            </td>
                                                            <td className="px-2 py-2 text-slate-600 dark:text-slate-400">
                                                                <SearchBar
                                                                    searchAPI={
                                                                        "v1/AutoSuggestion/GetDestinationSuggestion?Prefix="
                                                                    }
                                                                    paramCompanyId="companyId"
                                                                    selectReacord={(d) =>
                                                                        handleTranshipPortChange(
                                                                            index,
                                                                            "transPortCity",
                                                                            d?.[1] || "",
                                                                        )
                                                                    }
                                                                    value={port.transPortCity}
                                                                    onchange={(val) => {
                                                                        if (!val)
                                                                            handleTranshipPortChange(
                                                                                index,
                                                                                "transPortCity",
                                                                                "",
                                                                            );
                                                                    }}
                                                                />
                                                            </td>
                                                            <td className="px-2 py-2 text-slate-600 dark:text-slate-400">
                                                                <DateInputwithcal
                                                                    label="ETA"
                                                                    name="transEta"
                                                                    value={port.transEta}
                                                                    onChange={(name, value) =>
                                                                        handleTranshipPortChange(
                                                                            index,
                                                                            "transEta",
                                                                            value,
                                                                        )
                                                                    }
                                                                />
                                                            </td>
                                                            <td className="px-2 py-2 text-slate-600 dark:text-slate-400">
                                                                <SearchBar
                                                                    searchAPI={
                                                                        "v1/AutoSuggestion/GetVesselList?Prefix="
                                                                    }
                                                                    paramCompanyId="companyId"
                                                                    selectReacord={(d) =>
                                                                        handleTranshipPortChange(
                                                                            index,
                                                                            "transVessel",
                                                                            d?.[1] || "",
                                                                        )
                                                                    }
                                                                    value={port.transVessel}
                                                                    onchange={(val) => {
                                                                        if (!val)
                                                                            handleTranshipPortChange(
                                                                                index,
                                                                                "transVessel",
                                                                                "",
                                                                            );
                                                                    }}
                                                                />
                                                            </td>
                                                            <td className="px-2 py-2 text-slate-600 dark:text-slate-400">
                                                                <DateInputwithcal
                                                                    label="ETD"
                                                                    name="transEtd"
                                                                    value={port.transEtd}
                                                                    onChange={(name, value) =>
                                                                        handleTranshipPortChange(
                                                                            index,
                                                                            "transEtd",
                                                                            value,
                                                                        )
                                                                    }
                                                                />
                                                            </td>
                                                            <td className="px-2 py-2 text-slate-600 dark:text-slate-400">
                                                                <Input
                                                                    className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus:ring-1 focus:ring-[#0072FF]"
                                                                    value={port.transVoyage}
                                                                    onChange={(e) =>
                                                                        handleTranshipPortChange(
                                                                            index,
                                                                            "transVoyage",
                                                                            e.target.value,
                                                                        )
                                                                    }
                                                                />
                                                            </td>
                                                            <td className="px-2 py-2 text-slate-600 dark:text-slate-400">
                                                                <button
                                                                    type="button"
                                                                    className="p-2 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 transition"
                                                                    title="Delete"
                                                                    onClick={() =>
                                                                        handleTranshipPortChange(
                                                                            index,
                                                                            "isDeleted",
                                                                            true,
                                                                        )
                                                                    }
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ),
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                <button
                                    type="button"
                                    onClick={addTransshipPort}
                                    className="mt-4 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                                >
                                    <Plus className="w-4 h-4" /> Add Port
                                </button>
                            </motion.div>
                        )}
                    </SectionCard>

          {/* ── PARTICULARS ── */}
          <SectionCard ref={particularsRef}>
            <SectionHeader
              icon={Package}
              title="Particulars Furnished by Shipper"
              color="orange"
              section="particulars"
              expandedSections={expandedSections}
              toggleSection={toggleSection}
            />
            {expandedSections.particulars && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                className="p-4 space-y-4"
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                        <Settings className="w-4 h-4 text-white" />
                      </div>
                      <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                        Print Options
                      </h4>
                    </div>
                    <div className="space-y-2">
                      <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                        Weight & Measurement on B/L
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { value: "B", label: "Both" },
                          { value: "M", label: "Metric" },
                          { value: "E", label: "English" },
                        ].map((opt) => (
                          <label
                            key={opt.value}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all ${formData?.WeightPrintOption === opt.value
                              ? "bg-blue-500 text-white shadow-md"
                              : "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 hover:border-blue-300"
                              }`}
                          >
                            <input
                              type="radio"
                              name="WeightPrintOption"
                              value={opt.value}
                              checked={
                                formData?.WeightPrintOption === opt.value
                              }
                              onChange={handleInputChange}
                              className="sr-only"
                            />
                            {opt.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                        <Hash className="w-4 h-4 text-white" />
                      </div>
                      <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                        Calculation
                      </h4>
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-white dark:hover:bg-slate-700/50 transition-colors cursor-pointer">
                        <input
                          type="checkbox"
                          name="DoNotAutoCalculate"
                          checked={formData?.DoNotAutoCalculate ?? true}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-emerald-500 rounded border-slate-300 focus:ring-emerald-500"
                        />
                        <span className="text-xs text-slate-600 dark:text-slate-300">
                          Do not auto calculate
                        </span>
                      </label>
                      <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-white dark:hover:bg-slate-700/50 transition-colors cursor-pointer">
                        <input
                          type="checkbox"
                          name="DoNotPrintNetWeight"
                          checked={formData?.DoNotPrintNetWeight || false}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-emerald-500 rounded border-slate-300 focus:ring-emerald-500"
                        />
                        <span className="text-xs text-slate-600 dark:text-slate-300">
                          Do not print Net Weight
                        </span>
                      </label>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-purple-500 flex items-center justify-center">
                        <Printer className="w-4 h-4 text-white" />
                      </div>
                      <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                        Print Format
                      </h4>
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-white dark:hover:bg-slate-700/50 transition-colors cursor-pointer">
                        <input
                          type="checkbox"
                          name="IsPrintGroupCommodities"
                          checked={formData?.IsPrintGroupCommodities || false}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-purple-500 rounded border-slate-300 focus:ring-purple-500"
                        />
                        <span className="text-xs text-slate-600 dark:text-slate-300">
                          Print in Group Commodities
                        </span>
                      </label>
                      <label className="flex items-center gap-2 p-2 rounded-lg hover:bg-white dark:hover:bg-slate-700/50 transition-colors cursor-pointer">
                        <input
                          type="checkbox"
                          name="IsPrintwithRiderList"
                          checked={formData?.IsPrintwithRiderList || false}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-purple-500 rounded border-slate-300 focus:ring-purple-500"
                        />
                        <span className="text-xs text-slate-600 dark:text-slate-300">
                          Print with RIDER LIST
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-900/20 dark:via-indigo-900/20 dark:to-purple-900/20 rounded-xl border border-blue-100 dark:border-blue-800/30">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400 mr-2">
                      Import:
                    </span>
                    <button
                      type="button"
                      onClick={() => navigate("/booking")}
                      className="px-3 py-1.5 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-lg text-xs font-medium transition-all shadow-sm hover:shadow-md flex items-center gap-1.5"
                    >
                      <Upload className="w-3 h-3" /> Dock Receipt
                    </button>
                    <button
                      type="button"
                      className="px-3 py-1.5 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white rounded-lg text-xs font-medium transition-all shadow-sm hover:shadow-md flex items-center gap-1.5"
                    >
                      <Upload className="w-3 h-3" /> MBL
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate("/booking")}
                      className="px-3 py-1.5 bg-gradient-to-r from-sky-500 to-blue-500 hover:from-sky-600 hover:to-blue-600 text-white rounded-lg text-xs font-medium transition-all shadow-sm hover:shadow-md flex items-center gap-1.5"
                    >
                      <Package className="w-3 h-3" /> Booking Cargo
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="px-3 py-1.5 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-lg text-xs font-medium transition-all border border-slate-200 dark:border-slate-600 flex items-center gap-1.5"
                    >
                      <FileText className="w-3 h-3" /> Fill Description
                    </button>
                    <button
                      type="button"
                      className="px-3 py-1.5 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-lg text-xs font-medium transition-all border border-slate-200 dark:border-slate-600 flex items-center gap-1.5"
                    >
                      <Settings className="w-3 h-3" /> Override
                    </button>
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                  <div
                    className="grid bg-gradient-to-r from-slate-700 to-slate-800 dark:from-slate-800 dark:to-slate-900 text-white text-xs font-semibold"
                    style={{
                      gridTemplateColumns:
                        "120px 100px 160px 1fr 120px 120px 120px 60px",
                    }}
                  >
                    <div className="px-3 py-3 text-center border-r border-slate-600">
                      CONTAINER
                    </div>
                    <div className="px-3 py-3 text-center border-r border-slate-600">
                      CONT. TYPE
                    </div>
                    <div className="px-3 py-3 text-center border-r border-slate-600">
                      NO. OF PKG
                    </div>
                    <div className="px-3 py-3 text-center border-r border-slate-600">
                      CARGO DESCRIPTION
                    </div>
                    <div className="px-3 py-3 text-center border-r border-slate-600">
                      NET WEIGHT
                    </div>
                    <div className="px-3 py-3 text-center border-r border-slate-600">
                      GROSS WEIGHT
                    </div>
                    <div className="px-3 py-3 text-center border-r border-slate-600">
                      MEASUREMENT
                    </div>
                    <div className="px-3 py-3 text-center">DEL</div>
                  </div>
                  <div className="overflow-x-auto bg-white dark:bg-slate-900">
                    <div className="min-w-[1000px]">
                      {rows.map(
                        (row, index) =>
                          row?.IS_DELETED == false && (
                            <div
                              key={index}
                              className="grid border-t border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                              style={{
                                gridTemplateColumns:
                                  "120px 100px 160px 1fr 120px 120px 120px 60px",
                              }}
                            >
                              <div className="p-2 space-y-1.5 border-r border-slate-100 dark:border-slate-700/50">
                                <input
                                  value={row.CONTAINER_NUMBER}
                                  onChange={(e) =>
                                    _handleInputChange(
                                      index,
                                      "CONTAINER_NUMBER",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="Container No"
                                  className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                />
                                <input
                                  value={row.SEAL_NUMBER}
                                  onChange={(e) =>
                                    _handleInputChange(
                                      index,
                                      "SEAL_NUMBER",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="Seal No"
                                  className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                />
                                <textarea
                                  rows={3}
                                  value={row.MARKS_NO}
                                  onChange={(e) =>
                                    _handleInputChange(
                                      index,
                                      "MARKS_NO",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="Marks No"
                                  className="w-full min-h-[70px] resize-y rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                />
                              </div>
                              <div className="p-2 space-y-1.5 border-r border-slate-100 dark:border-slate-700/50">
                                <select
                                  value={row?.CONT_TYPE_CODE || ""}
                                  onChange={(e) => {
                                    _handleInputChange(
                                      index,
                                      "CONT_TYPE_CODE",
                                      e.target.value,
                                    );
                                    _handleInputChange(
                                      index,
                                      "CONT_TYPE",
                                      e.target.options[e.target.selectedIndex]
                                        .text,
                                    );
                                  }}
                                  className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                >
                                  <option value="">Select</option>
                                  {containerTypeList?.map((container, key) => (
                                    <option
                                      key={key}
                                      value={container.moveTypeId}
                                    >
                                      {container.moveType}
                                    </option>
                                  ))}
                                </select>
                                <input
                                  type="number"
                                  min="0"
                                  value={row.TARE_WEIGHT}
                                  onChange={(e) =>
                                    _handleInputChange(
                                      index,
                                      "TARE_WEIGHT",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="Tare Weight"
                                  className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                />
                                {/* <button
                                  type="button"
                                  className="w-full h-7 rounded-md bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white text-[10px] font-medium transition-all shadow-sm flex items-center justify-center gap-1"
                                >
                                  <Check className="w-3 h-3" /> Validate VIN
                                </button>
                                <div className="grid grid-cols-2 gap-1">
                                  <input
                                    value={row.TITLE_NUMBER}
                                    onChange={(e) =>
                                      _handleInputChange(
                                        index,
                                        "TITLE_NUMBER",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Title No"
                                    className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                  />
                                  <input
                                    value={row.CARGO_STATE}
                                    onChange={(e) =>
                                      _handleInputChange(
                                        index,
                                        "CARGO_STATE",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="State"
                                    className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                  />
                                </div> */}
                              </div>
                              <div className="p-2 space-y-1.5 border-r border-slate-100 dark:border-slate-700/50">
                                <div className="grid grid-cols-2 gap-1">
                                  <input
                                    value={row.NO_PACKAGE}
                                    onChange={(e) =>
                                      _handleInputChange(
                                        index,
                                        "NO_PACKAGE",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Qty"
                                    className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                  />
                                  <select
                                    value={row.PACKAGE_CODE}
                                    onChange={(e) =>
                                      _handleInputChange(
                                        index,
                                        "PACKAGE_CODE",
                                        e.target.value,
                                      )
                                    }
                                    className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-1 text-[10px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                  >
                                    <option value="">Select type</option>
                                    {packageTypeList?.map((item, index) => (
                                      <option key={index} value={item.package}>
                                        {item.package}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <input
                                  value={row.HAZ_CODE}
                                  onChange={(e) =>
                                    _handleInputChange(
                                      index,
                                      "HAZ_CODE",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="HAZ Code"
                                  className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                />
                                <input
                                  value={row.VIN_NUMBER}
                                  onChange={(e) =>
                                    _handleInputChange(
                                      index,
                                      "VIN_NUMBER",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="VIN Number"
                                  className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                />
                                <button
                                  type="button"
                                  className="w-full h-7 rounded-md bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white text-[10px] font-medium transition-all shadow-sm flex items-center justify-center gap-1"
                                >
                                  <Check className="w-3 h-3" /> Validate VIN
                                </button>
                                <div className="grid grid-cols-2 gap-1">
                                  <input
                                    value={row.TITLE_NUMBER}
                                    onChange={(e) =>
                                      _handleInputChange(
                                        index,
                                        "TITLE_NUMBER",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="Title No"
                                    className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                  />
                                  <input
                                    value={row.CARGO_STATE}
                                    onChange={(e) =>
                                      _handleInputChange(
                                        index,
                                        "CARGO_STATE",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="State"
                                    className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                  />
                                </div>
                              </div>
                              <div className="p-2 border-r border-slate-100 dark:border-slate-700/50">
                                <textarea
                                  rows={8}
                                  value={row.DESCRIPTIONS}
                                  onChange={(e) =>
                                    _handleInputChange(
                                      index,
                                      "DESCRIPTIONS",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="Enter detailed cargo description..."
                                  className="w-full h-full min-h-[180px] resize-y rounded-md border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-3 py-2 text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                />
                              </div>
                              <div className="p-2 space-y-2 border-r border-slate-100 dark:border-slate-700/50">
                                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-2 space-y-1.5">
                                  <label className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                                    Net KGS
                                  </label>
                                  <input
                                    value={row.NET_KGS}
                                    onChange={(e) =>
                                      handleWeightChange(
                                        index,
                                        "NET_KGS",
                                        e.target.value,
                                      )
                                    }
                                    className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                  />
                                </div>
                                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-2 space-y-1.5">
                                  <label className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                                    Net LBS
                                  </label>
                                  <input
                                    value={row.NET_LBS}
                                    onChange={(e) =>
                                      handleWeightChange(
                                        index,
                                        "NET_LBS",
                                        e.target.value,
                                      )
                                    }
                                    className="w-full h-7 rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                  />
                                </div>
                              </div>
                              <div className="p-2 space-y-2 border-r border-slate-100 dark:border-slate-700/50">
                                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2 space-y-1.5">
                                  <label className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide">
                                    KGS
                                  </label>
                                  <input
                                    value={row.KGS}
                                    onChange={(e) =>
                                      handleWeightChange(
                                        index,
                                        "KGS",
                                        e.target.value,
                                      )
                                    }
                                    className="w-full h-7 rounded-md border border-blue-200 dark:border-blue-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                  />
                                </div>
                                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2 space-y-1.5">
                                  <label className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide">
                                    LBS
                                  </label>
                                  <input
                                    value={row.LBS}
                                    onChange={(e) =>
                                      handleWeightChange(
                                        index,
                                        "LBS",
                                        e.target.value,
                                      )
                                    }
                                    className="w-full h-7 rounded-md border border-blue-200 dark:border-blue-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                  />
                                </div>
                              </div>
                              <div className="p-2 space-y-2 border-r border-slate-100 dark:border-slate-700/50">
                                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-2 space-y-1.5">
                                  <label className="text-[10px] font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide">
                                    CBM
                                  </label>
                                  <input
                                    value={row.CBM}
                                    onChange={(e) =>
                                      handleWeightChange(
                                        index,
                                        "CBM",
                                        e.target.value,
                                      )
                                    }
                                    className="w-full h-7 rounded-md border border-purple-200 dark:border-purple-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                  />
                                </div>
                                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-2 space-y-1.5">
                                  <label className="text-[10px] font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide">
                                    CFT
                                  </label>
                                  <input
                                    value={row.CFT}
                                    onChange={(e) =>
                                      handleWeightChange(
                                        index,
                                        "CFT",
                                        e.target.value,
                                      )
                                    }
                                    className="w-full h-7 rounded-md border border-purple-200 dark:border-purple-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 px-2 text-xs focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                                  />
                                </div>
                              </div>
                              <div className="p-2 flex items-center justify-center">
                                <button
                                  type="button"
                                  onClick={() => _handleInputChange(index, "IS_DELETED", true)}
                                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/40 hover:text-red-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                  disabled={rows.length === 1}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ),
                      )}
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 py-3 flex justify-center border-t border-slate-200 dark:border-slate-700">
                    <button
                      type="button"
                      onClick={addRow}
                      className="px-5 py-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-lg font-medium text-sm transition-all shadow-md hover:shadow-lg flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" /> Add Container Row
                    </button>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-slate-50 to-white dark:from-slate-800/50 dark:to-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                      <Weight className="w-4 h-4 text-white" />
                    </div>
                    <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                      Total Summary
                    </h4>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      {
                        label: "Containers",
                        value: formData?.TotalContainer || "0",
                        color: "",
                      },
                      {
                        label: "Packages",
                        value: formData?.TotalPackage || "0",
                        color: "",
                      },
                      {
                        label: "Gross KGS",
                        value: formData?.TotalKgs || "0.00",
                        color: "",
                      },
                      {
                        label: "Gross LBS",
                        value: formData?.TotalLbs || "0.00",
                        color: "",
                      },
                      {
                        label: "Net KGS",
                        value: formData?.TotalNetKgs || "0.00",
                        color: "text-emerald-600 dark:text-emerald-400",
                      },
                      {
                        label: "Net LBS",
                        value: formData?.TotalNetLbs || "0.00",
                        color: "text-emerald-600 dark:text-emerald-400",
                      },
                      {
                        label: "CBM",
                        value: formData?.TotalCbm || "0.00",
                        color: "text-blue-600 dark:text-blue-400",
                      },
                      {
                        label: "CFT",
                        value: formData?.TotalCft || "0.00",
                        color: "text-blue-600 dark:text-blue-400",
                      },
                    ].map((item) => (
                      <div
                        key={item.label}
                        className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-slate-100 dark:border-slate-700"
                      >
                        <p className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wide mb-1">
                          {item.label}
                        </p>
                        <p
                          className={`text-lg font-bold ${item.color || "text-slate-800 dark:text-slate-200"}`}
                        >
                          {item.value}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/10 dark:to-orange-900/10 rounded-xl border border-amber-200 dark:border-amber-800/30">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                      <FileText className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Show L/C or Manual Particulars Furnished by Shipper
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    {[
                      { label: "Yes", value: true, active: "bg-amber-500" },
                      { label: "No", value: false, active: "bg-slate-500" },
                    ].map((opt) => (
                      <label
                        key={opt.label}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium cursor-pointer transition-all ${formData?.IsLCManualDetails === opt.value
                          ? `${opt.active} text-white shadow-md`
                          : "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600"
                          }`}
                      >
                        <input
                          type="radio"
                          name="IsLCManualDetails"
                          value={String(opt.value)}
                          checked={formData?.IsLCManualDetails === opt.value}
                          onChange={(e) =>
                            handleInputChange({
                              target: { name: e.target.name, value: opt.value },
                            })
                          }
                          className="sr-only"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </SectionCard>

                    {/* ── PAYMENT & PRINT ── */}
                    <div ref={paymentPrintRef} className="space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <SectionCard>
                                <SectionHeader
                                    icon={CreditCard}
                                    title="HBL Payment Mode"
                                    color="purple"
                                    section="payment"
                                    expandedSections={expandedSections}
                                    toggleSection={toggleSection}
                                />
                                {expandedSections.payment && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        className="p-5"
                                    >
                                        <div className="flex gap-4">
                                            {[
                                                { label: "Prepaid", value: "P" },
                                                { label: "Collect", value: "C" },
                                                { label: "No Status", value: "N" },
                                            ].map((item) => (
                                                <label
                                                    key={item.value}
                                                    className={`flex-1 p-4 rounded-xl border-2 cursor-pointer transition-all ${formData?.HBLPaymentMode === item.value.trimEnd() ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20" : "border-slate-200 dark:border-slate-700 hover:border-pink-300"}`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div
                                                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${formData?.HBLPaymentMode === item.value.trimEnd() ? "border-purple-500 bg-purple-500" : "border-slate-300"}`}
                                                        >
                                                            {formData?.HBLPaymentMode ===
                                                                item.value.trimEnd() && (
                                                                    <Check className="w-3 h-3 text-white" />
                                                                )}
                                                        </div>
                                                        <span className="font-medium text-slate-900 dark:text-white">
                                                            {item.label}
                                                        </span>
                                                    </div>
                                                    <input
                                                        type="radio"
                                                        name="HBLPaymentMode"
                                                        value={item.value}
                                                        checked={
                                                            formData?.HBLPaymentMode === item.value.trimEnd()
                                                        }
                                                        onChange={handleInputChange}
                                                        className="fixed opacity-0 pointer-events-none"
                                                    />
                                                </label>
                                            ))}
                                        </div>
                                        <div className="mb-4 mt-4">
                                            {(formData?.HBLPaymentMode === "N") && (
                                                <InputField label="Payment Mode">
                                                    <input
                                                        type="text"
                                                        name="PaymentMode"
                                                        value={formData?.PaymentMode}
                                                        onChange={handleInputChange}
                                                        className={inputClass}
                                                    />
                                                </InputField>
                                            )}
                                        </div>
                                    </motion.div>
                                )}
                            </SectionCard>
                            <SectionCard>
                                <SectionHeader
                                    icon={CreditCard}
                                    title="HBL Release Mode"
                                    color="pink"
                                    section="payment"
                                    expandedSections={expandedSections}
                                    toggleSection={toggleSection}
                                />
                                {expandedSections.payment && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        className="p-5"
                                    >
                                        <div className="mb-4">
                                            <div className="grid grid-cols-2 gap-3">
                                                {[
                                                    { label: "Original", value: "O" },
                                                    { label: "Express", value: "E" },
                                                    { label: "Telex Release", value: "R" },
                                                    { label: "Release Original at Destination", value: "D" },
                                                ].map((item) => (
                                                    <label
                                                        key={item.value}
                                                        className={`flex-1 p-4 rounded-xl border-2 cursor-pointer transition-all ${formData?.HBLReleaseMode === item.value ? "border-pink-500 bg-pink-50 dark:bg-pink-900/20" : "border-slate-200 dark:border-slate-700 hover:border-pink-300"}`}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div
                                                                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${formData?.HBLReleaseMode === item.value ? "border-pink-500 bg-pink-500" : "border-slate-300"}`}
                                                            >
                                                                {formData?.HBLReleaseMode === item.value && (
                                                                    <Check className="w-3 h-3 text-white" />
                                                                )}
                                                            </div>
                                                            <span className="font-medium text-slate-900 dark:text-white">
                                                                {item.label}
                                                            </span>
                                                        </div>
                                                        <input
                                                            type="radio"
                                                            name="HBLReleaseMode"
                                                            value={item.value}
                                                            checked={formData?.HBLReleaseMode === item.value}
                                                            onChange={handleInputChange}
                                                            className="fixed opacity-0 pointer-events-none"
                                                        />
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </SectionCard>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <SectionCard>
                                <SectionHeader
                                    icon={Printer}
                                    title="B/L Charges Description"
                                    color="indigo"
                                    section="printing"
                                    expandedSections={expandedSections}
                                    toggleSection={toggleSection}
                                />
                                {expandedSections.printing && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        className="p-5"
                                    >
                                        <div className="space-y-4">
                                            {[
                                                "InternalRemark1",
                                                "InternalRemark2",
                                                "InternalRemark3",
                                                "InternalRemark4",
                                            ].map((name, i) => (
                                                <InputField
                                                    key={name}
                                                    label={`Internal Remark ${i + 1}`}
                                                >
                                                    <textarea
                                                        name={name}
                                                        value={formData?.[name]}
                                                        onChange={handleInputChange}
                                                        rows="2"
                                                        placeholder="Enter Internal Remark..."
                                                        className={`${inputClass} mt-2`}
                                                    />
                                                </InputField>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </SectionCard>
                            <SectionCard>
                                <SectionHeader
                                    icon={Printer}
                                    title="B/L Printing Options"
                                    color="teal"
                                    section="printing"
                                    expandedSections={expandedSections}
                                    toggleSection={toggleSection}
                                />
                                {expandedSections.printing && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        className="p-5"
                                    >
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <InputField label="Declared Value">
                                                <input
                                                    type="text"
                                                    name="PrintDeclaredValue"
                                                    value={formData?.PrintDeclaredValue}
                                                    onChange={handleInputChange}
                                                    className={inputClass}
                                                />
                                            </InputField>
                                            <InputField label="No. Original (Print)">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    name="NoOriginal"
                                                    value={formData?.NoOriginal}
                                                    onChange={handleInputChange}
                                                    className={inputClass}
                                                />
                                            </InputField>
                                            <InputField label="On-Board">
                                                <input
                                                    type="checkbox"
                                                    name="OnBoardPrint"
                                                    checked={formData?.OnBoardPrint || false}
                                                    onChange={handleInputChange}
                                                    className="h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                                                />
                                            </InputField>
                                            <InputField label="Hide BL Sign">
                                                <input
                                                    type="checkbox"
                                                    name="IsHideBLSign"
                                                    checked={formData?.IsHideBLSign || false}
                                                    onChange={handleInputChange}
                                                    className="h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                                                />
                                            </InputField>
                                            <InputField label="Freight Prepaid At">
                                                <SearchBar
                                                    searchAPI={
                                                        "v1/AutoSuggestion/GetDestinationSuggestion?Prefix="
                                                    }
                                                    paramCompanyId="companyId"
                                                    selectReacord={(d) => {
                                                        handleInputChange({
                                                            target: { name: "FreightPayable", value: d?.[1] },
                                                        });
                                                        handleInputChange({
                                                            target: {
                                                                name: "FreightPayableCode",
                                                                value: String(d?.[0] ?? ""),
                                                            },
                                                        });
                                                    }}
                                                    onchange={(val) => {
                                                        if (!val) {
                                                            handleInputChange({
                                                                target: { name: "FreightPayable", value: "" },
                                                            });
                                                            handleInputChange({
                                                                target: {
                                                                    name: "FreightPayableCode",
                                                                    value: "",
                                                                },
                                                            });
                                                        }
                                                    }}
                                                    value={formData?.FreightPayable}
                                                />
                                            </InputField>
                                            <InputField label="Place Of Issue" required>
                                                <SearchBar
                                                    searchAPI={
                                                        "v1/AutoSuggestion/GetDestinationSuggestion?Prefix="
                                                    }
                                                    paramCompanyId="companyId"
                                                    selectReacord={(d) => {
                                                        handleInputChange({
                                                            target: { name: "PlaceOfIssue", value: d?.[1] },
                                                        });
                                                        handleInputChange({
                                                            target: {
                                                                name: "PlaceOfIssueCode",
                                                                value: String(d?.[0] ?? ""),
                                                            },
                                                        });
                                                    }}
                                                    onchange={(val) => {
                                                        if (!val) {
                                                            handleInputChange({
                                                                target: { name: "PlaceOfIssue", value: "" },
                                                            });
                                                            handleInputChange({
                                                                target: { name: "PlaceOfIssueCode", value: "" },
                                                            });
                                                        }
                                                    }}
                                                    value={formData?.PlaceOfIssue}
                                                />
                                            </InputField>
                                            <InputField label="BL Sign">
                                                <input
                                                    type="text"
                                                    name="BLSignature"
                                                    value={formData?.BLSignature}
                                                    onChange={handleInputChange}
                                                    className={inputClass}
                                                />
                                            </InputField>
                                            <InputField label="Issue Date">
                                                <DateInputwithcal
                                                    onError={handleDateError}
                                                    label="Issue Date"
                                                    name="IssueDate"
                                                    value={formData?.IssueDate}
                                                    onChange={handleDateChange}
                                                    required
                                                />
                                            </InputField>
                                            <InputField label="Received for Shipment">
                                                <DateInputwithcal
                                                    onError={handleDateError}
                                                    label="Received for Shipment"
                                                    name="ReceiveShipment"
                                                    value={formData?.ReceiveShipment}
                                                    onChange={handleDateChange}
                                                    required
                                                />
                                            </InputField>
                                        </div>
                                    </motion.div>
                                )}
                            </SectionCard>
                        </div>
                        <div className="p-3 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 overflow-hidden hover:shadow-md dark:shadow-none dark:hover:shadow-[0_0_12px_rgba(59,130,246,0.25)] transition-shadow duration-200">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <div className="text-sm text-slate-500 dark:text-slate-400">
                                    <span className="text-red-500">*</span> Required fields must
                                    be filled
                                </div>
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    <button
                                        type="button"
                                        onClick={() => navigate(-1)}
                                        className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-4 h-[40px] rounded-[9px] transition-all duration-150 text-slate-600 hover:bg-red-50 dark:text-slate-300 dark:hover:bg-red-900/20"
                                    >
                                        <X className="w-[15px] h-[15px]" />
                                        <span className="hidden sm:inline">Cancel</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            window.open(
                                                `/hblPrint?bookingId=${bookingId}&FILENUMBER=${encodeURIComponent(FILENUMBER)}`,
                                                "_blank",
                                            );
                                        }}
                                        className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-4 h-[40px] rounded-[9px] transition-all duration-150 bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-600"
                                    >
                                        <Printer className="w-[15px] h-[15px]" />
                                        <span className="hidden sm:inline">Print</span>
                                    </button>
                                    <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-1" />
                                    <button
                                        type="button"
                                        onClick={handleSave}
                                        className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-5 h-[40px] rounded-[9px] transition-all duration-150 bg-green-600 text-white hover:bg-green-700 shadow-sm hover:shadow-green-500/30"
                                    >
                                        <Send className="w-[15px] h-[15px]" />
                                        <span>Submit HBL</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        </>
    );
};

export default HBL;